/**
 * Optimistic user bubble: rendered immediately on send, before the backend's
 * echo lands in Firestore (see `useOptimisticTurns`/`lib/optimisticMessages`).
 *
 * - `status: 'sending'` → same visual as a real user bubble, dimmed slightly
 *   so it reads as "not yet confirmed".
 * - `status: 'error'`, `likelySent: false` → error styling + "Tentar de novo" /
 *   "Descartar", pt-BR. Never disappears on its own.
 * - `status: 'error'`, `likelySent: true` → a real message matching this text
 *   already showed up (send likely went through despite the client-side
 *   error). Offers only "Descartar" — `sendMessage` isn't idempotent, so
 *   retrying here would duplicate the message server-side.
 */
import { FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import type { RenderableTurn } from '@/hooks/useOptimisticTurns'
import { cn } from '@/lib/utils'

export function PendingUserBubble({
  turn,
  onRetry,
  onDiscard,
}: {
  turn: RenderableTurn
  onRetry: (localId: string) => void
  onDiscard: (localId: string) => void
}) {
  const isError = turn.status === 'error'
  const likelySent = isError && turn.likelySent
  return (
    <div className="flex flex-col items-end gap-1.5">
      <div
        className={cn(
          'max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 sm:max-w-[75%]',
          isError
            ? 'border-destructive/40 bg-destructive/10 text-destructive border'
            : 'bg-primary text-primary-foreground opacity-70',
        )}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{turn.text}</p>
        {turn.attachments.length > 0 ? (
          <div className="mt-2 flex flex-wrap justify-end gap-1.5">
            {turn.attachments.map((att) => (
              <span
                key={att.storagePath}
                className="bg-background/20 flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs"
              >
                <FileText className="size-3.5 shrink-0" />
                <span className="truncate">{att.name}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {isError ? (
        <div className="flex items-center gap-1 pr-1">
          <p className="text-destructive max-w-[70%] text-right text-[0.6875rem]">
            {likelySent
              ? 'Sua mensagem já chegou.'
              : (turn.errorMessage ?? 'Não foi possível enviar. Tente novamente.')}
          </p>
          {likelySent ? null : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[0.6875rem]"
              onClick={() => onRetry(turn.localId)}
            >
              Tentar de novo
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-6 px-2 text-[0.6875rem]"
            onClick={() => onDiscard(turn.localId)}
          >
            Descartar
          </Button>
        </div>
      ) : null}
    </div>
  )
}
